// ==========================================
// FILE: \App.js
// ==========================================

import React, { useState, useEffect } from 'react';
import { 
  ActivityIndicator, View, Linking, Text, TouchableOpacity, 
  StyleSheet, Modal, LogBox, Platform, ScrollView 
} from 'react-native';
import { NavigationContainer } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './src/config/firebase';
import * as Updates from 'expo-updates';
import Constants from 'expo-constants';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { AlertCircle, Sparkles, RefreshCw, DownloadCloud, Info } from 'lucide-react-native';

// --- IMPORT REVENUECAT ---
import Purchases from 'react-native-purchases';
import { WineProvider, useWineContext } from './src/context/WineContext';
import { ConsentProvider } from './src/components/CookieConsentBanner';

import LoginScreen from './src/screens/LoginScreen';
import HomeScreen from './src/screens/HomeScreen';
import AddWineScreen from './src/screens/AddWineScreen';
import CaveauScreen from './src/screens/CaveauScreen';
import CatalogScreen from './src/screens/CatalogScreen';
import DetailScreen from './src/screens/DetailScreen';
import ProfileScreen from './src/screens/ProfileScreen';
import StatsScreen from './src/screens/StatsScreen';
import MapScreen from './src/screens/MapScreen';
import PaywallScreen from './src/screens/PaywallScreen'; 

const Stack = createNativeStackNavigator();

LogBox.ignoreLogs([
  'props.pointerEvents is deprecated',
  '"textShadow*" style props are deprecated',
  'react-native-maps'
]);

const APP_VERSION = parseInt(Constants.expoConfig?.android?.versionCode || Constants.nativeBuildVersion || '1', 10);

// CHIAVI API REVENUECAT (Pubbliche, sicure da esporre nel client)
const API_KEY_ANDROID = "goog_wDNSxnrfFykHLsfCnqMmDxBEBQO";

// --- SOTTO-COMPONENTE NAVIGATOR PER PASSARE LA LINGUA REATTIVA ---
function AppNavigator({ user, customAlert, closeAppAlert }) {
  // Consumiamo il contesto dei vini per tracciare i cambiamenti di lingua e lo stato del profilo
  const { userProfile, isLoadingWines, language } = useWineContext();

  // Un utente è considerato conforme se il suo profilo Firestore esiste, ha accettato i termini,
  // ha dichiarato la maggiore età e ha configurato con successo la password di sicurezza.
  const isCompliant = userProfile && userProfile.termsAccepted && userProfile.isAdult && userProfile.hasSecurePassword;

  // Gestione della verifica e-mail per gli account standard password (i social bypassano questo controllo)
  const isEmailVerified = user ? (user.providerData[0]?.providerId !== 'password' || user.emailVerified) : false;

  // Mostriamo la LoginScreen se l'utente non è loggato, non è verificato, o non ha completato l'onboarding GDPR
  const shouldShowLogin = !user || !isEmailVerified || !isCompliant;

  return (
    <ConsentProvider appLanguage={language || userProfile?.language || 'it'}>
      <View style={{ flex: 1 }}>
        <NavigationContainer>
          <Stack.Navigator screenOptions={{ headerShown: false }}>
            {shouldShowLogin ? (
              <Stack.Screen name="Login" component={LoginScreen} />
            ) : (
              <Stack.Group>
                <Stack.Screen name="Home" component={HomeScreen} />
                <Stack.Screen name="Caveau" component={CaveauScreen} />
                <Stack.Screen name="Stats" component={StatsScreen} />
                <Stack.Screen name="Map" component={MapScreen} />
                <Stack.Screen name="Catalog" component={CatalogScreen} />
                <Stack.Screen name="AddWine" component={AddWineScreen} />
                <Stack.Screen name="Detail" component={DetailScreen} />
                <Stack.Screen name="Profile" component={ProfileScreen} />
                <Stack.Screen name="Paywall" component={PaywallScreen} />
              </Stack.Group>
            )}
          </Stack.Navigator>
        </NavigationContainer>

        {/* 🌟 OVERLAY DI CARICAMENTO GLOBALE ASSOLUTO 
            Mantiene montato l'albero di navigazione e preserva gli stati locali della LoginScreen 
            (incluso il modale onboardingUser) evitando che i form vengano distrutti durante il caricamento */}
        {user && isLoadingWines && (
          <View style={[StyleSheet.absoluteFill, { backgroundColor: '#f8fafc', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }]}>
            <ActivityIndicator size="large" color="#e11d48" />
          </View>
        )}
      </View>
    </ConsentProvider>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  const [needsApkUpdate, setNeedsApkUpdate] = useState(false);
  const [apkUrl, setApkUrl] = useState("");
  const [latestVersion, setLatestVersion] = useState(0);

  const [customAlert, setCustomAlert] = useState({ 
    visible: false, title: '', message: '', type: 'info', onConfirm: null, confirmText: 'OK', icon: null
  });

  const showAppAlert = (title, message, type = 'info', onConfirm = null, confirmText = 'OK', icon = null) => {
    setCustomAlert({ visible: true, title, message, type, onConfirm, confirmText, icon });
  };

  const closeAppAlert = () => {
    setCustomAlert(prev => ({ ...prev, visible: false }));
  };

  // --- INIZIALIZZAZIONE REVENUECAT BLINDATA ---
  useEffect(() => {
    const setupRevenueCat = async () => {
      if (Platform.OS === 'web' || Constants.executionEnvironment === 'storeClient') {
        console.log("🛡️ WebApp o Expo Go rilevati: RevenueCat bypassato in sicurezza per evitare errori.");
        return; 
      }

      try {
        Purchases.setLogLevel(Purchases.LOG_LEVEL.DEBUG); 
        
        if (Platform.OS === 'android') {
          Purchases.configure({ apiKey: API_KEY_ANDROID });
          console.log("💰 RevenueCat Inizializzato con successo su Android!");
        } 
      } catch (error) {
        console.error("Errore inizializzazione RevenueCat:", error);
      }
    };

    setupRevenueCat();
  }, []);

  // --- CONTROLLO AGGIORNAMENTI E POPUP ---
  useEffect(() => {
    const checkVersionsAndUpdates = async () => {
      try {
        // 1. CONTROLLO AGGIORNAMENTO FORZATO APK
        const versionRef = doc(db, 'config', 'appVersion');
        const versionSnap = await getDoc(versionRef);

        if (versionSnap.exists()) {
          const data = versionSnap.data();
          const minApk = parseInt(data.minApkVersion, 10);
          setLatestVersion(minApk);

          if (APP_VERSION < minApk && Platform.OS !== 'web') {
            setApkUrl(data.apkUrl);
            setNeedsApkUpdate(true);
            return; 
          }
        }

        // 2. CONTROLLO AGGIORNAMENTI OTA (Over The Air)
        if (!__DEV__ && Platform.OS !== 'web') {
          const update = await Updates.checkForUpdateAsync();
          if (update.isAvailable) {
            await Updates.fetchUpdateAsync();
            showAppAlert(
              "Aggiornamento Pronto",
              "Abbiamo scaricato nuove funzioni! Riavvia l'app per applicarle.",
              "update",
              () => Updates.reloadAsync(),
              "Riavvia Ora",
              <RefreshCw color="#3b82f6" size={28} />
            );
            return; 
          }
        }

        // 3. POPUP CLOUD AUTOMATICO
        const alertRef = doc(db, 'config', 'appAlert');
        const alertSnap = await getDoc(alertRef);

        if (alertSnap.exists()) {
          const alertData = alertSnap.data();
          
          if (alertData.isActive && alertData.alertId) {
            const hasSeenAlert = await AsyncStorage.getItem(alertData.alertId);
            
            if (!hasSeenAlert) {
              setTimeout(() => {
                showAppAlert(
                  alertData.title || "Novità in VinoLogic!",
                  alertData.message || "Scopri i nuovi aggiornamenti.",
                  "changelog",
                  async () => {
                    await AsyncStorage.setItem(alertData.alertId, 'true');
                    closeAppAlert();
                  },
                  alertData.buttonText || "Scopri",
                  <Sparkles color="#e11d48" size={28} />
                );
              }, 1000);
            }
          }
        }

      } catch (error) {
        console.log("Errore controllo aggiornamenti/alert:", error);
      }
    };

    checkVersionsAndUpdates();
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setIsLoading(false);
    });
    return unsubscribe;
  }, []);

  if (isLoading) {
    return (
      <SafeAreaProvider>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#f8fafc' }}>
          <ActivityIndicator size="large" color="#e11d48" />
        </View>
      </SafeAreaProvider>
    );
  }

  if (needsApkUpdate) {
    return (
      <SafeAreaProvider>
        <ScrollView contentContainerStyle={styles.updateContainer}>
          <View style={styles.updateIconBg}>
            <DownloadCloud color="#e11d48" size={60} />
          </View>
          <Text style={styles.updateTitle}>Nuova Versione Disponibile</Text>
          <Text style={styles.updateText}>
            Stai usando la v{APP_VERSION}. Per continuare a usare VinoLogic, devi installare la versione v{latestVersion}.
          </Text>

          <TouchableOpacity style={styles.updateButton} onPress={() => Linking.openURL(apkUrl)}>
            <Text style={styles.updateButtonText}>Scarica Aggiornamento v{latestVersion}</Text>
          </TouchableOpacity>

          <View style={styles.instructionsCard}>
            <View style={{flexDirection: 'row', alignItems: 'center', marginBottom: 10}}>
              <Info color="#0f172a" size={18} />
              <Text style={styles.instructionsTitle}>Come installare?</Text>
            </View>
            <Text style={styles.instructionStep}>1. Clicca il bottone rosso qui sopra.</Text>
            <Text style={styles.instructionStep}>2. Una volta terminato il download, tocca la notifica "Download completato".</Text>
            <Text style={styles.instructionStep}>3. Se Android lo chiede, consenti l'installazione dal browser.</Text>
            <Text style={styles.instructionStep}>4. Riavvia l'app e goditi le novità!</Text>
          </View>
        </ScrollView>
      </SafeAreaProvider>
    );
  }

  return (
    <SafeAreaProvider>
      <View style={{ flex: 1 }}>
        <WineProvider>
          <AppNavigator 
            user={user} 
            customAlert={customAlert} 
            closeAppAlert={closeAppAlert} 
          />
        </WineProvider>

        {/* CUSTOM ALERT POPUP */}
        <Modal transparent={true} visible={customAlert.visible} animationType="fade">
          <View style={styles.modalOverlay}>
            <View style={styles.modalBox}>
              <View style={styles.modalHeader}>
                {customAlert.icon ? customAlert.icon : <AlertCircle color="#3b82f6" size={28} />}
                <Text style={styles.modalTitle}>{customAlert.title}</Text>
              </View>
              <Text style={styles.modalMessage}>{customAlert.message}</Text>
              <View style={styles.modalButtons}>
                <TouchableOpacity 
                  style={[styles.modalConfirmBtn, customAlert.type === 'update' ? { backgroundColor: '#3b82f6' } : { backgroundColor: '#e11d48' }]} 
                  onPress={() => { if (customAlert.onConfirm) { customAlert.onConfirm(); } else { closeAppAlert(); } }}
                >
                  <Text style={styles.modalConfirmText}>{customAlert.confirmText}</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>
      </View>
    </SafeAreaProvider>
  );
}

const styles = StyleSheet.create({
  updateContainer: { flexGrow: 1, justifyContent: 'center', alignItems: 'center', padding: 30, backgroundColor: '#f8fafc' },
  updateIconBg: { width: 100, height: 100, borderRadius: 50, backgroundColor: '#fff1f2', alignItems: 'center', justifyContent: 'center', marginBottom: 24 },
  updateTitle: { fontSize: 24, fontWeight: 'bold', color: '#0f172a', marginBottom: 12, textAlign: 'center' },
  updateText: { fontSize: 15, color: '#64748b', textAlign: 'center', marginBottom: 30, lineHeight: 22 },
  updateButton: { backgroundColor: '#e11d48', paddingVertical: 18, paddingHorizontal: 24, borderRadius: 16, width: '100%', alignItems: 'center', elevation: 4, marginBottom: 30 },
  updateButtonText: { color: 'white', fontWeight: 'bold', fontSize: 16 },
  instructionsCard: { backgroundColor: 'white', padding: 20, borderRadius: 20, width: '100%', borderWidth: 1, borderColor: '#e2e8f0' },
  instructionsTitle: { fontSize: 16, fontWeight: 'bold', color: '#0f172a', marginLeft: 8 },
  instructionStep: { fontSize: 14, color: '#475569', marginBottom: 8, lineHeight: 20 },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(15, 23, 42, 0.7)', justifyContent: 'center', alignItems: 'center', padding: 20 },
  modalBox: { backgroundColor: 'white', borderRadius: 24, padding: 24, width: '100%', maxWidth: 400, elevation: 10 },
  modalHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 16 },
  modalTitle: { fontSize: 20, fontWeight: 'bold', color: '#0f172a', marginLeft: 12, flex: 1 },
  modalMessage: { fontSize: 16, color: '#475569', lineHeight: 24, marginBottom: 24 },
  modalButtons: { flexDirection: 'row', justifyContent: 'flex-end', gap: 12 },
  modalConfirmBtn: { paddingVertical: 12, paddingHorizontal: 20, borderRadius: 12, minWidth: 100, alignItems: 'center' },
  modalConfirmText: { color: 'white', fontWeight: 'bold', fontSize: 16 }
});